import type { UpdateBookingBody, UpdateBookingBodyBookingType } from '@api/models';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useToast } from '@ui/components/common/toast';
import {
  BookingFormData,
  BookingSubmissionSchema,
  needsAddressForSlot,
  transformBookingDataToApiPayload,
  validateBookingForm,
  validateCurrentStep
} from '@website/app/[locale]/activity/[id]/slots/lib';
import { useBookingContext } from '@website/context/BookingContext';
import { ApiError, BrowserApiClient } from '@website/lib/api/browser';
import { retryOnBookingConflict } from '@website/utils/retryOnConflict';

import { StepName } from './useBookingSteps';

interface StepButton {
  text: string;
  onClick: () => void;
  disabled: boolean;
  variant: 'primary' | 'secondary';
  loading?: boolean;
}

export interface UseStepButtonResult {
  stepButton: StepButton | null;
  showPaymentMethodSelector: boolean;
  setShowPaymentMethodSelector: (show: boolean) => void;
  handlePaymentMethodSelected: (method: 'cash' | 'online') => void;
}

export const useStepButton = (currentStep: StepName): UseStepButtonResult => {
  const t = useTranslations();
  const router = useRouter();
  const { error: showErrorToast, success: showSuccessToast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentMethodSelector, setShowPaymentMethodSelector] = useState(false);

  const form = useFormContext<BookingFormData>();
  const formData = form.watch();

  // Get the slot's available payment methods
  const slotPaymentMethods = formData.selectedSlotPaymentMethods;
  const isCashOnly = slotPaymentMethods?.length === 1 && slotPaymentMethods[0] === 'cash';
  const isOnlineOnly = slotPaymentMethods?.length === 1 && slotPaymentMethods[0] === 'online';
  const hasBothPaymentMethods = slotPaymentMethods?.length === 2;

  const bookingContext = useBookingContext();
  const { bookingFlow, isAtHomeActivity, activityId, bookingData, isEditMode, locale, isPaymentFlow } = bookingContext;

  // Address is needed only for an in-person AT-HOME session, gated on the CHOSEN
  // slot's resolved 3-value kind (a mixed activity returns isAtHomeActivity:false
  // at the activity level, so we MUST key off the picked slot, not the activity).
  // Shared helper so the stepper, this router, and the validators stay in lockstep.
  const needsAddress = needsAddressForSlot(formData.slotLocation?.kind, isAtHomeActivity, formData.locationKind);

  // Validate current step using form data
  const isValid = useMemo(() => {
    const { success } = validateCurrentStep(formData, currentStep, isAtHomeActivity);
    return success;
  }, [formData, currentStep, isAtHomeActivity]);

  const validationMessage = useMemo(() => {
    const { errors } = validateCurrentStep(formData, currentStep, isAtHomeActivity);
    return errors.length > 0 ? errors[0] : '';
  }, [formData, currentStep, isAtHomeActivity]);

  // Handle validation errors with user-friendly toasts
  const handleValidationError = useCallback(
    (message: string) => {
      showErrorToast(message);
    },
    [showErrorToast]
  );

  // Handle booking submission and navigate to status step
  const handleBookingSubmission = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    // Set status to pending and go to status step
    bookingFlow.updateBookingStatus('pending');
    bookingFlow.goToStep('status');

    try {
      // Get current form data
      const currentFormData = form.getValues();

      // Ensure required fields are populated
      const slotId = currentFormData.slotId || '';
      const finalActivityId = currentFormData.activityId || activityId || '';

      // Validate required fields before proceeding
      if (!slotId) {
        throw new Error(t('Booking.errors.slotIdRequired'));
      }

      if (!finalActivityId) {
        throw new Error(t('Booking.errors.activityIdRequired'));
      }

      if (!currentFormData.bookingDate) {
        throw new Error(t('Booking.errors.bookingDateRequired'));
      }

      if (!currentFormData.timeSlot) {
        throw new Error(t('Booking.errors.timeSlotRequired'));
      }

      // Validate using comprehensive schema
      const validationResult = validateBookingForm(currentFormData, isAtHomeActivity);
      if (!validationResult.success) {
        throw new Error(`${t('Booking.errors.validationFailed')}: ${validationResult.errors.join(', ')}`);
      }

      // Additional submission schema validation
      try {
        BookingSubmissionSchema.parse(currentFormData);
      } catch (schemaError: any) {
        console.error('Schema validation error:', schemaError);
        throw new Error(`${t('Booking.errors.checkBookingDetails')}: ${schemaError.message}`);
      }

      if (isEditMode && bookingData) {
        if (!currentFormData.paymentMethod) {
          const methods = currentFormData.selectedSlotPaymentMethods;
          if (methods && methods.length === 1) {
            currentFormData.paymentMethod = methods[0];
          } else if (bookingData.paymentStatus === 'CASH') {
            currentFormData.paymentMethod = 'cash';
          } else {
            currentFormData.paymentMethod = 'online';
          }
        }
        const bookingPayload = transformBookingDataToApiPayload(currentFormData);

        const urlParams = new URLSearchParams(window.location.search);
        const originalStep = urlParams.get('step');

        let updatePayload: UpdateBookingBody = {};

        if (originalStep === 'select-address' && currentStep === 'select-address') {
          updatePayload = {
            addressLine1: bookingPayload.addressLine1,
            addressCity: bookingPayload.addressCity,
            addressState: bookingPayload.addressState,
            addressZipCode: bookingPayload.addressZipCode,
            addressCountry: bookingPayload.addressCountry,
            // Must thread the geocoded coords (like the sibling branches below): an
            // at_home location with a radius hard-rejects a coord-less address, and a
            // radius-less one would silently null the stored coords, breaking
            // live-resolve distance/display.
            addressLatitude: bookingPayload.addressLatitude,
            addressLongitude: bookingPayload.addressLongitude,
            notes: bookingPayload.notes
          };
        } else if (originalStep === 'select-time' || currentStep === 'select-time') {
          updatePayload = {
            activityId: bookingPayload.activityId,
            slotId: bookingPayload.slotId,
            bookingType: bookingPayload.bookingType as unknown as UpdateBookingBodyBookingType,
            bookingDate: bookingPayload.bookingDate,
            startTime: bookingPayload.startTime,
            endTime: bookingPayload.endTime,
            numberOfPeople: bookingPayload.numberOfPeople,
            notes: bookingPayload.notes
          };

          // Keep the address whenever the CHOSEN slot needs one (per-slot rule), not
          // just when the activity is at-home: a mixed activity reports
          // isAtHomeActivity:false yet still has at_home slots that require an address.
          if (
            needsAddressForSlot(currentFormData.slotLocation?.kind, isAtHomeActivity, currentFormData.locationKind) &&
            bookingPayload.addressLine1
          ) {
            updatePayload.addressLine1 = bookingPayload.addressLine1;
            updatePayload.addressCity = bookingPayload.addressCity;
            updatePayload.addressState = bookingPayload.addressState;
            updatePayload.addressZipCode = bookingPayload.addressZipCode;
            updatePayload.addressCountry = bookingPayload.addressCountry;
            updatePayload.addressLatitude = bookingPayload.addressLatitude;
            updatePayload.addressLongitude = bookingPayload.addressLongitude;
          }
        } else {
          updatePayload = {
            notes: bookingPayload.notes !== bookingData.notes ? bookingPayload.notes : undefined,
            numberOfPeople: bookingPayload.numberOfPeople
          };
        }

        const response = await BrowserApiClient.updateBooking(bookingData.id, updatePayload);

        if (!response || !response.id) {
          throw new Error(t('Booking.errors.invalidBookingResponse'));
        }

        // Invalidate bookings queries to refresh the bookings list
        await queryClient.invalidateQueries({ queryKey: ['bookings'] });
        await queryClient.invalidateQueries({ queryKey: ['professional-bookings'] });
        await queryClient.invalidateQueries({ queryKey: ['activity-bookings'] });

        // Keep booking ID in form state so status step can always resolve selected booking
        form.setValue('bookingId', response.id);

        bookingFlow.updateBookingStatus('success', response.id);
      } else {
        const bookingPayload = transformBookingDataToApiPayload(currentFormData);

        const response = await retryOnBookingConflict(() => BrowserApiClient.createBooking(bookingPayload));

        if (!response || !response.id) {
          throw new Error(t('Booking.errors.invalidBookingResponse'));
        }

        // Invalidate bookings queries to refresh the bookings list
        await queryClient.invalidateQueries({ queryKey: ['bookings'] });
        await queryClient.invalidateQueries({ queryKey: ['professional-bookings'] });
        await queryClient.invalidateQueries({ queryKey: ['activity-bookings'] });

        // Keep booking ID in form state so status step can always resolve selected booking
        form.setValue('bookingId', response.id);

        // Update status to success
        bookingFlow.updateBookingStatus('success', response.id);
      }
    } catch (error: any) {
      console.error('Booking creation failed:', error);

      const errorMsg: string = error.message || '';

      if (
        errorMsg.includes('cannot book your own activity') ||
        errorMsg.includes('You cannot book your own activity')
      ) {
        bookingFlow.updateBookingStatus('own_activity');
        setIsSubmitting(false);
        return;
      }

      let errorMessage = t('Booking.errors.unexpectedError');

      if (errorMsg.includes('time slot is no longer available') || errorMsg.includes('already booked')) {
        // Conflict (409): someone else took the window/seat first - show the
        // localized copy instead of the raw API sentence.
        errorMessage = t('Booking.errors.slotNoLongerAvailable');
      } else if (error instanceof ApiError) {
        // API returned a structured error - surface the message directly
        errorMessage = errorMsg;
      } else if (errorMsg.includes('Slot ID is required')) {
        errorMessage = t('Booking.errors.selectTimeSlot');
      } else if (errorMsg.includes('Activity ID is required')) {
        errorMessage = t('Booking.errors.activityInfoMissing');
      } else if (errorMsg.includes('Validation failed') || errorMsg.includes('Please check your booking details')) {
        errorMessage = errorMsg;
      } else if (errorMsg.includes('Authentication')) {
        errorMessage = t('Booking.errors.authenticationError');
      } else if (errorMsg.includes('Network')) {
        errorMessage = t('Booking.errors.networkError');
      } else if (errorMsg.includes('missing booking ID')) {
        errorMessage = t('Booking.errors.serverError');
      } else if (errorMsg.includes('already has a booking that conflicts')) {
        errorMessage = errorMsg.includes('client')
          ? t('Booking.errors.clientBookingConflict')
          : t('Booking.errors.bookingConflict');
      } else if (errorMsg) {
        errorMessage = errorMsg;
      }

      showErrorToast(errorMessage);

      // Update status to failed
      bookingFlow.updateBookingStatus('failed');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    form,
    bookingFlow,
    isAtHomeActivity,
    activityId,
    showErrorToast,
    queryClient,
    t,
    isEditMode,
    bookingData,
    currentStep
  ]);

  // Create booking with PENDING_PAYMENT and navigate to payment step
  // This is now idempotent - if an active booking already exists for this slot, it will be returned
  const handleCreateBookingAndGoToPayment = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const currentFormData = form.getValues();

      // Validate required fields
      const slotId = currentFormData.slotId || '';
      const finalActivityId = currentFormData.activityId || activityId || '';

      if (!slotId) {
        throw new Error(t('Booking.errors.slotIdRequired'));
      }
      if (!finalActivityId) {
        throw new Error(t('Booking.errors.activityIdRequired'));
      }
      if (!currentFormData.bookingDate) {
        throw new Error(t('Booking.errors.bookingDateRequired'));
      }
      if (!currentFormData.timeSlot) {
        throw new Error(t('Booking.errors.timeSlotRequired'));
      }

      // Validate using comprehensive schema
      const validationResult = validateBookingForm(currentFormData, isAtHomeActivity);
      if (!validationResult.success) {
        throw new Error(`${t('Booking.errors.validationFailed')}: ${validationResult.errors.join(', ')}`);
      }

      // Additional submission schema validation
      try {
        BookingSubmissionSchema.parse(currentFormData);
      } catch (schemaError: any) {
        console.error('Schema validation error:', schemaError);
        throw new Error(`${t('Booking.errors.checkBookingDetails')}: ${schemaError.message}`);
      }

      // Create the booking (idempotent - returns existing if same slot)
      const bookingPayload = transformBookingDataToApiPayload(currentFormData);
      const response = await retryOnBookingConflict(() => BrowserApiClient.createBooking(bookingPayload));

      if (!response || !response.id) {
        throw new Error(t('Booking.errors.invalidBookingResponse'));
      }

      // Check if the booking is already completed (user went back in history after completing)
      const completedStatuses = ['PAID', 'CASH', 'FREE'];
      if (completedStatuses.includes(response.paymentStatus || '')) {
        // Booking already completed - redirect to booking detail page
        showSuccessToast(t('Booking.messages.bookingAlreadyCompleted'));
        router.push(`/${locale}/bookings/${response.id}`);
        return;
      }

      // Store the booking ID in form state for payment step
      form.setValue('bookingId', response.id);

      // Navigate to payment step
      bookingFlow.goToStep('payment');
    } catch (error: any) {
      console.error('Booking creation for payment failed:', error);

      // Check for provider not accepting payments error
      if (error.message?.includes('PROVIDER_NOT_ACCEPTING_PAYMENTS')) {
        showErrorToast(t('Booking.payment.providerNotAccepting'));
        return;
      }

      // Check for own activity error
      if (
        error.message?.includes('cannot book your own activity') ||
        error.message?.includes('You cannot book your own activity')
      ) {
        bookingFlow.updateBookingStatus('own_activity');
        bookingFlow.goToStep('status');
        return;
      }

      // Check for booking conflict error
      if (error.message?.includes('already has a booking that conflicts')) {
        const msg = error.message.includes('client')
          ? t('Booking.errors.clientBookingConflict')
          : t('Booking.errors.bookingConflict');
        showErrorToast(msg);
        return;
      }

      showErrorToast(error.message || t('Booking.errors.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    form,
    bookingFlow,
    isAtHomeActivity,
    activityId,
    showErrorToast,
    showSuccessToast,
    t,
    router,
    locale
  ]);

  // Update existing PENDING_PAYMENT booking (if slot changed) and navigate to payment
  // Used when editing a booking that hasn't been paid yet
  const handleUpdateBookingAndGoToPayment = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const currentFormData = form.getValues();

      // Validate required fields
      const slotId = currentFormData.slotId || '';
      const finalActivityId = currentFormData.activityId || activityId || '';

      if (!slotId) {
        throw new Error(t('Booking.errors.slotIdRequired'));
      }
      if (!finalActivityId) {
        throw new Error(t('Booking.errors.activityIdRequired'));
      }
      if (!currentFormData.bookingDate) {
        throw new Error(t('Booking.errors.bookingDateRequired'));
      }
      if (!currentFormData.timeSlot) {
        throw new Error(t('Booking.errors.timeSlotRequired'));
      }

      // Validate using comprehensive schema
      const validationResult = validateBookingForm(currentFormData, isAtHomeActivity);
      if (!validationResult.success) {
        throw new Error(`${t('Booking.errors.validationFailed')}: ${validationResult.errors.join(', ')}`);
      }

      // Additional submission schema validation
      try {
        BookingSubmissionSchema.parse(currentFormData);
      } catch (schemaError: any) {
        console.error('Schema validation error:', schemaError);
        throw new Error(`${t('Booking.errors.checkBookingDetails')}: ${schemaError.message}`);
      }

      if (!bookingData?.id) {
        throw new Error(t('Booking.errors.bookingIdMissing'));
      }

      // Ensure paymentMethod is set for the transform function
      if (!currentFormData.paymentMethod) {
        const methods = currentFormData.selectedSlotPaymentMethods;
        if (methods && methods.length === 1) {
          currentFormData.paymentMethod = methods[0];
        } else if (bookingData.paymentStatus === 'CASH') {
          currentFormData.paymentMethod = 'cash';
        } else {
          currentFormData.paymentMethod = 'online';
        }
      }

      // Build update payload
      const bookingPayload = transformBookingDataToApiPayload(currentFormData);
      const updatePayload: UpdateBookingBody = {
        activityId: bookingPayload.activityId,
        slotId: bookingPayload.slotId,
        bookingType: bookingPayload.bookingType as unknown as UpdateBookingBodyBookingType,
        bookingDate: bookingPayload.bookingDate,
        startTime: bookingPayload.startTime,
        endTime: bookingPayload.endTime,
        numberOfPeople: bookingPayload.numberOfPeople,
        notes: bookingPayload.notes
      };

      // Keep the address whenever the CHOSEN slot needs one (per-slot rule), not
      // just when the activity is at-home: a mixed activity reports
      // isAtHomeActivity:false yet still has at_home slots that require an address.
      if (
        needsAddressForSlot(currentFormData.slotLocation?.kind, isAtHomeActivity, currentFormData.locationKind) &&
        bookingPayload.addressLine1
      ) {
        updatePayload.addressLine1 = bookingPayload.addressLine1;
        updatePayload.addressCity = bookingPayload.addressCity;
        updatePayload.addressState = bookingPayload.addressState;
        updatePayload.addressZipCode = bookingPayload.addressZipCode;
        updatePayload.addressCountry = bookingPayload.addressCountry;
        updatePayload.addressLatitude = bookingPayload.addressLatitude;
        updatePayload.addressLongitude = bookingPayload.addressLongitude;
      }

      // Update the booking
      const response = await BrowserApiClient.updateBooking(bookingData.id, updatePayload);

      if (!response || !response.id) {
        throw new Error(t('Booking.errors.invalidBookingResponse'));
      }

      // Ensure the booking ID is in form state for payment step
      form.setValue('bookingId', response.id);

      // Navigate to payment step
      bookingFlow.goToStep('payment');
    } catch (error: any) {
      console.error('Booking update for payment failed:', error);

      let errorMessage = t('Booking.errors.unexpectedError');
      if (error.message) {
        // Check for payment status related errors
        if (error.message.includes('Cannot change time slot for a paid booking')) {
          errorMessage = t('Booking.errors.cannotChangeSlotPaidBooking');
        } else if (error.message.includes('Cannot update booking with status')) {
          errorMessage = t('Booking.errors.bookingCannotBeUpdated');
        } else if (error.message.includes('already has a booking that conflicts')) {
          errorMessage = error.message.includes('client')
            ? t('Booking.errors.clientBookingConflict')
            : t('Booking.errors.bookingConflict');
        } else {
          errorMessage = error.message;
        }
      }
      showErrorToast(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, form, bookingFlow, isAtHomeActivity, activityId, bookingData, showErrorToast, t]);

  // Handle new booking based on selected payment method
  // Both cash and online go to PaymentStep (PaymentStep shows appropriate UI)
  const handleNewBookingWithPaymentMethod = useCallback(
    async (paymentMethod: 'cash' | 'online') => {
      form.setValue('paymentMethod', paymentMethod);
      await handleCreateBookingAndGoToPayment();
    },
    [form, handleCreateBookingAndGoToPayment]
  );

  // Handler for when user selects a payment method from the selector modal
  const handlePaymentMethodSelected = useCallback(
    (method: 'cash' | 'online') => {
      setShowPaymentMethodSelector(false);
      handleNewBookingWithPaymentMethod(method);
    },
    [handleNewBookingWithPaymentMethod]
  );

  // Handle continue for new bookings - branches based on available payment methods
  const handleNewBookingContinue = useCallback(() => {
    // Free bookings skip the payment method selector entirely: the server transitions
    // the booking to FREE via confirmBooking regardless of which method we send.
    const isFreeBooking = !formData.basePrice || formData.basePrice <= 0;
    if (isFreeBooking) {
      const fallbackMethod: 'cash' | 'online' = isOnlineOnly ? 'online' : 'cash';
      form.setValue('paymentMethod', formData.paymentMethod || fallbackMethod);
      handleCreateBookingAndGoToPayment();
      return;
    }

    if (isCashOnly) {
      form.setValue('paymentMethod', 'cash');
      handleCreateBookingAndGoToPayment();
    } else if (isOnlineOnly) {
      form.setValue('paymentMethod', 'online');
      handleCreateBookingAndGoToPayment();
    } else if (hasBothPaymentMethods) {
      setShowPaymentMethodSelector(true);
    } else {
      form.setValue('paymentMethod', 'online');
      handleCreateBookingAndGoToPayment();
    }
  }, [
    isCashOnly,
    isOnlineOnly,
    hasBothPaymentMethods,
    form,
    formData.basePrice,
    formData.paymentMethod,
    handleCreateBookingAndGoToPayment
  ]);

  const stepButton = useMemo((): StepButton | null => {
    switch (currentStep) {
      case 'select-time': {
        const timeStepButtonText = needsAddress
          ? t('Booking.buttons.next')
          : isPaymentFlow
            ? t('Booking.buttons.next')
            : isEditMode
              ? t('Booking.buttons.update')
              : t('Booking.buttons.continue');

        return {
          text: timeStepButtonText,
          onClick: () => {
            if (isValid && formData.bookingDate && formData.timeSlot) {
              if (needsAddress) {
                bookingFlow.goToStep('select-address');
              } else if (isPaymentFlow) {
                handleUpdateBookingAndGoToPayment();
              } else if (isEditMode) {
                handleBookingSubmission();
              } else {
                handleNewBookingContinue();
              }
            } else {
              handleValidationError(validationMessage);
            }
          },
          disabled: !isValid || isSubmitting,
          variant: 'primary',
          loading: isSubmitting && !needsAddress && (isEditMode || isPaymentFlow)
        };
      }

      case 'select-address': {
        const addressStepButtonText = isPaymentFlow
          ? t('Booking.buttons.next')
          : isEditMode
            ? t('Booking.buttons.update')
            : t('Booking.buttons.continue');

        return {
          text: addressStepButtonText,
          onClick: () => {
            if (isValid && formData.address) {
              if (isPaymentFlow) {
                handleUpdateBookingAndGoToPayment();
              } else if (isEditMode) {
                handleBookingSubmission();
              } else {
                handleNewBookingContinue();
              }
            } else {
              handleValidationError(validationMessage);
            }
          },
          disabled: !isValid || isSubmitting,
          variant: 'primary',
          loading: isSubmitting
        };
      }

      default:
        return null;
    }
  }, [
    currentStep,
    formData,
    needsAddress,
    isEditMode,
    isPaymentFlow,
    isValid,
    validationMessage,
    handleValidationError,
    handleBookingSubmission,
    handleUpdateBookingAndGoToPayment,
    handleNewBookingContinue,
    isSubmitting,
    t,
    bookingFlow
  ]);

  return {
    stepButton,
    showPaymentMethodSelector,
    setShowPaymentMethodSelector,
    handlePaymentMethodSelected
  };
};
